import { groupBy, mapValues } from 'lodash-es';
import { createContext, useContext, useMemo } from 'react';
import { RouteObject } from 'react-router';

import { useSelector } from '@tdp/core';

import { ISectionedDetailsPage, IDetailsRouteSection } from './abstractions';
import {
  createNavigationContext,
  NavigationRouteData,
} from './createNavigationContext';
import { userContextSlice } from '../auth';

type SectionDetailsContext<TData> = Pick<
  IDetailsRouteSection<TData>,
  'id' | 'data'
>[];

export interface NavigationSectionRouteData<TData = undefined> {
  id: string;
  data?: TData;
  isActive: boolean;
  routes: NavigationRouteData<TData>[];
}

/**
 * An augmented version of the @see createNavigationContext that allows for 'sectioned' navigation where you have groups of routes
 * where the groups also can contain data.
 */
export function createSectionedNavigationContext<TData extends object>() {
  const SectionIdSymbol = Symbol('sectionId');

  // We attach the symbol to the route's 'data' so we can later extract which section the route belongs to.
  type InternalTData = TData & { [SectionIdSymbol]: string };
  const [useNavigationRoutesInternal, createRoutesInternal] =
    createNavigationContext<InternalTData>();
  // Keep the list of section ids in order here so we can pull them out later
  const SectionContext = createContext<
    SectionDetailsContext<TData> | undefined
  >(undefined);

  // wrapper around the un-sectioned create routes that injects the section data as context
  // and extracts the flattened list of routes for usage in the underlying createRoutes call.
  function createRoutes(
    definition: ISectionedDetailsPage<TData>
  ): RouteObject[] {
    const sectionData = definition.sections.map((section) => ({
      id: section.id,
      data: section.data,
    }));
    const layoutPromise = async () => {
      const layout = await definition.layout();
      // memoize to avoid triggering re-renders of the layout component

      return (
        <SectionContext.Provider value={sectionData}>
          {layout}
        </SectionContext.Provider>
      );
    };

    const routes = definition.sections.flatMap((section) =>
      section.routes.map((route) => ({
        ...route,
        data: {
          ...(route.data ?? {}),
          [SectionIdSymbol]: section.id,
        } as InternalTData,
      }))
    );

    return createRoutesInternal({
      layout: layoutPromise,
      routes,
    });
  }

  // Wrapper around the un-sectioned hook, that re-groups the routes into sections before returning them.
  function useNavigationRoutes() {
    const sectionData = useContext(SectionContext) ?? [];
    const internalRoutes = useNavigationRoutesInternal();
    const isMicrosoftTenant = !!useSelector(
      userContextSlice.selectors.isMicrosoftTenant
    );

    // Filter out sections with specific IDs if isMicrosoftTenant is false
    const [filteredSectionData, filteredInternalRoutes] = useMemo(() => {
      const alwaysExcludedSectionIds = ['error-pages'];
      if (!isMicrosoftTenant) {
        const excludedSectionIds = ['flights'];
        const excludedRoutePaths = [
          'first-party',
          'history',
          'new-flights',
          'withdraw',
          'app-contact-info',
        ];

        const allExcludedSectionIds = [...alwaysExcludedSectionIds, ...excludedSectionIds];

        const filteredSectionData = sectionData.filter(
          (section) => !allExcludedSectionIds.includes(section.id)
        );
        const filteredInternalRoutes = internalRoutes.filter(
          (route) => !excludedRoutePaths.includes(route.path)
        );
        return [filteredSectionData, filteredInternalRoutes];
      }

      const filteredSectionData = sectionData.filter(
        (section) => !alwaysExcludedSectionIds.includes(section.id)
      );

      return [filteredSectionData, internalRoutes];
    }, [isMicrosoftTenant, sectionData, internalRoutes]);

    const navData = useMemo(() => {
      const routeBySectionId = mapValues(
        groupBy(
          filteredInternalRoutes,
          (route) => route.data?.[SectionIdSymbol]
        ),
        (routes, sectionId) => ({
          id: sectionId,
          isActive: routes.some((route) => route.isActive),
          routes,
        })
      );
      return filteredSectionData.map((sectionData) => {
        const sectionRoute = routeBySectionId[sectionData.id];
        return {
          ...sectionData,
          ...sectionRoute,
        };
      });
    }, [filteredSectionData, filteredInternalRoutes]);

    return navData;
  }

  return [useNavigationRoutes, createRoutes] as const;
}

export type UseNavigationSectionedRoutesHook<TData extends object = object> =
  ReturnType<typeof createSectionedNavigationContext<TData>>[0];

export type CreateSectionedRoutes<TData extends object = object> = ReturnType<
  typeof createSectionedNavigationContext<TData>
>[1];
